import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../utils/config";
import "./PlantillesExplorador.css";

function PlantillesExplorador({
  idEmpresa,
  selectedPlantilla,
  onSelectPlantilla,
  selectedCategoria,
  onSelectCategoria,
  expandedNodes,
  onExpandedNodesChange,
  onGoToPartides,
  mode = "manage",
  onSelectForVersion,
}) {
  const isSelectMode = mode === "select";
  const [categories, setCategories] = useState([]);
  const [plantilles, setPlantilles] = useState([]);
  const [cercaCategoria, setCercaCategoria] = useState("");
  const [cercaPlantilla, setCercaPlantilla] = useState("");
  const [cercaGlobal, setCercaGlobal] = useState(false);
  const [incloureSubcategories, setIncloureSubcategories] = useState(true);
  const [modeVinculacio, setModeVinculacio] = useState(false);
  const [plantillaAVincular, setPlantillaAVincular] = useState(null);

  const [nouCategoriaVisible, setNouCategoriaVisible] = useState(false);
  const [nouCategoriaNom, setNouCategoriaNom] = useState("");
  const [editCategoriaId, setEditCategoriaId] = useState(null);
  const [editCategoriaNom, setEditCategoriaNom] = useState("");

  const [previewExpandedIds, setPreviewExpandedIds] = useState([]);
  const [previewPartides, setPreviewPartides] = useState({});

  const [nouPlantillaVisible, setNouPlantillaVisible] = useState(false);
  const [nouPlantillaNom, setNouPlantillaNom] = useState("");
  const [nouPlantillaDesc, setNouPlantillaDesc] = useState("");
  const [editPlantillaId, setEditPlantillaId] = useState(null);
  const [editPlantillaNom, setEditPlantillaNom] = useState("");
  const [editPlantillaDesc, setEditPlantillaDesc] = useState("");

  const carregarCategories = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/Categoriespressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa }),
      });
      const data = await resp.json();
      if (data.success) setCategories(data.categories || []);
    } catch (err) {
      console.error("Error carregant categories:", err);
    }
  }, [idEmpresa]);

  const carregarPlantilles = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/Plantillespressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa }),
      });
      const data = await resp.json();
      if (data.success) setPlantilles(data.plantilles || []);
    } catch (err) {
      console.error("Error carregant plantilles:", err);
    }
  }, [idEmpresa]);

  useEffect(() => {
    carregarCategories();
    carregarPlantilles();
  }, [carregarCategories, carregarPlantilles]);

  useEffect(() => {
    if (selectedPlantilla) {
      const fresh = plantilles.find(
        (p) => parseInt(p.id) === parseInt(selectedPlantilla.id)
      );
      if (fresh && fresh.categoriaIds !== selectedPlantilla.categoriaIds) {
        onSelectPlantilla(fresh);
      }
    }
  }, [plantilles, selectedPlantilla, onSelectPlantilla]);

  // --- Arbre de categories ---
  const buildTree = (cats, parentId = null) => {
    return cats
      .filter((c) => {
        const pare = c.idPare ? parseInt(c.idPare) : null;
        return pare === parentId;
      })
      .sort((a, b) => a.Nom.localeCompare(b.Nom));
  };

  const getDescendantIds = (catId) => {
    const directChildren = categories.filter(
      (c) => parseInt(c.idPare) === parseInt(catId)
    );
    let ids = [parseInt(catId)];
    directChildren.forEach((child) => {
      ids = ids.concat(getDescendantIds(child.id));
    });
    return ids;
  };

  const getCategoryPath = (catId) => {
    const parts = [];
    let current = categories.find((c) => parseInt(c.id) === parseInt(catId));
    while (current) {
      parts.unshift(current.Nom);
      current = current.idPare
        ? categories.find((c) => parseInt(c.id) === parseInt(current.idPare))
        : null;
    }
    return parts.join(" / ");
  };

  const toggleNode = (id) => {
    onExpandedNodesChange((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandPathTo = (catId) => {
    const newExpanded = { ...expandedNodes };
    let current = categories.find((c) => parseInt(c.id) === parseInt(catId));
    while (current && current.idPare) {
      newExpanded[current.idPare] = true;
      current = categories.find(
        (c) => parseInt(c.id) === parseInt(current.idPare)
      );
    }
    onExpandedNodesChange(newExpanded);
  };

  // --- Plantilles filtrades ---
  const getPlantillesFiltrades = () => {
    let filtered = plantilles;

    if (!cercaGlobal && selectedCategoria) {
      const catIds = incloureSubcategories
        ? getDescendantIds(selectedCategoria.id)
        : [parseInt(selectedCategoria.id)];

      filtered = filtered.filter((p) => {
        if (!p.categoriaIds) return false;
        const pCatIds = p.categoriaIds.split(",").map((x) => parseInt(x));
        return pCatIds.some((id) => catIds.includes(id));
      });
    }

    if (cercaPlantilla.trim()) {
      const term = cercaPlantilla.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.Nom.toLowerCase().includes(term) ||
          (p.Descripcio && p.Descripcio.toLowerCase().includes(term)) ||
          String(p.id).includes(term)
      );
    }

    return filtered;
  };

  const getCategoriesFiltrades = () => {
    if (!cercaCategoria.trim()) return [];
    const term = cercaCategoria.toLowerCase();
    return categories.filter((c) => c.Nom.toLowerCase().includes(term));
  };

  // --- CRUD Categories ---
  const handleCrearCategoria = async () => {
    if (!nouCategoriaNom.trim()) return;
    try {
      const resp = await fetch(`${API_URL}/Noucategoriapressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa,
          Nom: nouCategoriaNom.trim(),
          idPare: selectedCategoria ? selectedCategoria.id : null,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setNouCategoriaNom("");
        setNouCategoriaVisible(false);
        carregarCategories();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error creant categoria: " + err.message);
    }
  };

  const handleRenombrarCategoria = async () => {
    if (!editCategoriaNom.trim() || !editCategoriaId) return;
    try {
      const resp = await fetch(`${API_URL}/Modificarcategoriapressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editCategoriaId,
          Nom: editCategoriaNom.trim(),
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setEditCategoriaId(null);
        setEditCategoriaNom("");
        carregarCategories();
        if (
          selectedCategoria &&
          parseInt(selectedCategoria.id) === parseInt(editCategoriaId)
        ) {
          onSelectCategoria({ ...selectedCategoria, Nom: editCategoriaNom.trim() });
        }
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error renombrant categoria: " + err.message);
    }
  };

  const handleEliminarCategoria = async () => {
    if (!selectedCategoria) return;
    if (
      !window.confirm(
        `Segur que vols eliminar la categoria "${selectedCategoria.Nom}"?`
      )
    )
      return;
    try {
      const resp = await fetch(`${API_URL}/Esborrarcategoriapressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedCategoria.id }),
      });
      const data = await resp.json();
      if (data.success) {
        onSelectCategoria(null);
        carregarCategories();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error eliminant categoria: " + err.message);
    }
  };

  // --- CRUD Plantilles ---
  const handleCrearPlantilla = async () => {
    if (!nouPlantillaNom.trim()) return;
    try {
      const resp = await fetch(`${API_URL}/Nouplantillapressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa,
          Nom: nouPlantillaNom.trim(),
          Descripcio: nouPlantillaDesc.trim() || null,
          idCategoria: selectedCategoria ? selectedCategoria.id : null,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setNouPlantillaNom("");
        setNouPlantillaDesc("");
        setNouPlantillaVisible(false);
        carregarPlantilles();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error creant plantilla: " + err.message);
    }
  };

  const handleModificarPlantilla = async () => {
    if (!editPlantillaNom.trim() || !editPlantillaId) return;
    try {
      const resp = await fetch(`${API_URL}/Modificarplantillapressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editPlantillaId,
          Nom: editPlantillaNom.trim(),
          Descripcio: editPlantillaDesc.trim() || null,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setEditPlantillaId(null);
        carregarPlantilles();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error modificant plantilla: " + err.message);
    }
  };

  const handleEsborrarPlantilla = async () => {
    if (!selectedPlantilla) return;
    if (
      !window.confirm(
        `Segur que vols ESBORRAR la plantilla "${selectedPlantilla.Nom}"?\nAixò eliminarà la plantilla i totes les seves partides.`
      )
    )
      return;
    try {
      const resp = await fetch(`${API_URL}/Esborrarplantillapressupost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedPlantilla.id }),
      });
      const data = await resp.json();
      if (data.success) {
        onSelectPlantilla(null);
        carregarPlantilles();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error esborrant plantilla: " + err.message);
    }
  };

  // --- Vincular / Desvincular ---
  const handleIniciarVinculacio = () => {
    if (!selectedPlantilla) return;
    setPlantillaAVincular(selectedPlantilla);
    setModeVinculacio(true);
  };

  const handleCancelVinculacio = () => {
    setModeVinculacio(false);
    setPlantillaAVincular(null);
  };

  const handleVincularAqui = async () => {
    if (!plantillaAVincular || !selectedCategoria) return;
    try {
      const resp = await fetch(`${API_URL}/Vincularplantillacategoria.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idPlantilla: plantillaAVincular.id,
          idCategoria: selectedCategoria.id,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setModeVinculacio(false);
        setPlantillaAVincular(null);
        carregarPlantilles();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error vinculant: " + err.message);
    }
  };

  const handleDesvincular = async () => {
    if (!selectedPlantilla || !selectedCategoria) {
      alert("Selecciona una plantilla i una categoria per desvincular.");
      return;
    }

    const catIdsPlantilla = selectedPlantilla.categoriaIds
      ? selectedPlantilla.categoriaIds.split(",").length
      : 0;
    if (catIdsPlantilla <= 1) {
      alert(
        "No es pot desvincular: la plantilla només està vinculada a aquesta categoria.\nSi la vols eliminar, utilitza 'Esborrar'."
      );
      return;
    }

    if (
      !window.confirm(
        `Desvincular "${selectedPlantilla.Nom}" de la categoria "${selectedCategoria.Nom}"?\nLa plantilla seguirà existint a les altres categories.`
      )
    )
      return;

    try {
      const resp = await fetch(`${API_URL}/Desvincularplantillacategoria.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idPlantilla: selectedPlantilla.id,
          idCategoria: selectedCategoria.id,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        onSelectPlantilla(null);
        carregarPlantilles();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error desvinculant: " + err.message);
    }
  };

  // --- Render arbre ---
  const renderTree = (parentId = null, level = 0) => {
    const nodes = buildTree(categories, parentId);
    if (nodes.length === 0) return null;

    return (
      <ul className={`pe-tree ${level > 0 ? "pe-tree--nested" : "pe-tree--root"}`}>
        {nodes.map((cat) => {
          const hasChildren = categories.some(
            (c) => parseInt(c.idPare) === parseInt(cat.id)
          );
          const isSelected =
            selectedCategoria && parseInt(selectedCategoria.id) === parseInt(cat.id);
          const isExpanded = expandedNodes[cat.id];
          const isEditing = editCategoriaId === parseInt(cat.id);

          return (
            <li key={cat.id}>
              <div
                className={`pe-tree-node ${isSelected ? "pe-tree-node--selected" : ""}`}
                onClick={() => {
                  onSelectCategoria({ id: parseInt(cat.id), Nom: cat.Nom, idPare: cat.idPare });
                  if (hasChildren) toggleNode(cat.id);
                }}
              >
                <span className="pe-tree-expand-icon">
                  {hasChildren ? (isExpanded ? "▼" : "►") : "·"}
                </span>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={editCategoriaNom}
                      onChange={(e) => setEditCategoriaNom(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRenombrarCategoria();
                        if (e.key === "Escape") setEditCategoriaId(null);
                      }}
                      autoFocus
                      className="pe-tree-edit-input"
                    />
                    <button
                      className="Dashboard-btn-icona"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRenombrarCategoria();
                      }}
                      title="Confirmar"
                    >
                      ✓
                    </button>
                    <button
                      className="Dashboard-btn-icona"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditCategoriaId(null);
                      }}
                      title="Cancel·lar"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <span>{cat.Nom}</span>
                )}
              </div>
              {isExpanded && hasChildren && renderTree(parseInt(cat.id), level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  const plantillesFiltrades = getPlantillesFiltrades();
  const categoriesSearch = getCategoriesFiltrades();

  const togglePreviewPartides = async (idPlantilla) => {
    const id = parseInt(idPlantilla);
    if (previewExpandedIds.includes(id)) {
      setPreviewExpandedIds(prev => prev.filter(x => x !== id));
      return;
    }
    if (!previewPartides[id]) {
      try {
        const resp = await fetch(`${API_URL}/Partidesplantilla.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idPlantilla: id, idEmpresa: 0 }),
        });
        const data = await resp.json();
        if (data.success) {
          setPreviewPartides(prev => ({ ...prev, [id]: data.partides || [] }));
        }
      } catch (err) {
        console.error("Error carregant partides preview:", err);
      }
    }
    setPreviewExpandedIds(prev => [...prev, id]);
  };

  const renderPreviewPartides = (idPlantilla) => {
    const id = parseInt(idPlantilla);
    const partides = previewPartides[id];
    if (!partides || partides.length === 0) {
      return (
        <tr key={`preview-${id}`}>
          <td colSpan={6} style={{ padding: "4px 20px", color: "#999", fontStyle: "italic", background: "#f9f9f9" }}>
            Sense partides.
          </td>
        </tr>
      );
    }
    return partides.map((pp, idx) => (
      <tr key={`preview-${id}-${idx}`} style={{ background: "#f5f9ff" }}>
        <td></td>
        <td style={{ paddingLeft: `${((pp.Partida || "").split(".").length - 1) * 16 + 20}px`, fontSize: "0.9em", color: "#555" }}>
          {pp.EsApartat == 1 ? "📁" : "📄"} {pp.Partida}
        </td>
        <td style={{ fontSize: "0.9em", color: "#555" }}>{pp.DescripcioPartida || ""}</td>
        <td style={{ fontSize: "0.9em", color: "#555", textAlign: "right" }}>
          {pp.EsApartat == 1 ? "" : pp.Quantitat}
        </td>
        <td colSpan={2}></td>
      </tr>
    ));
  };

  return (
    <div className="pe-container">
      {/* Barra de mode vinculació */}
      {!isSelectMode && modeVinculacio && plantillaAVincular && (
        <div className="pe-vinculacio-bar">
          <strong>Mode vinculació</strong>
          <span>Plantilla: "{plantillaAVincular.Nom}"</span>
          <span className="pe-spacer" />
          <button
            className="Dashboard-btn Dashboard-btn-acceptar"
            onClick={handleVincularAqui}
            disabled={!selectedCategoria}
          >
            Vincular aquí
          </button>
          <button
            className="Dashboard-btn Dashboard-btn-cancelar"
            onClick={handleCancelVinculacio}
          >
            Cancel·lar
          </button>
        </div>
      )}

      {/* Zona superior: cercadors i checks */}
      <div className="Dashboard-form-section pe-cerca-zona">
        <div className="pe-cerca-camp">
          <label className="pe-cerca-label">Cercar categoria:</label>
          <input
            type="text"
            value={cercaCategoria}
            onChange={(e) => setCercaCategoria(e.target.value)}
            placeholder="Nom categoria..."
            className="pe-cerca-input pe-cerca-input--categoria"
          />
        </div>
        <div className="pe-cerca-camp">
          <label className="pe-cerca-label">Cercar plantilla:</label>
          <input
            type="text"
            value={cercaPlantilla}
            onChange={(e) => setCercaPlantilla(e.target.value)}
            placeholder="Nom, id, descripció..."
            className="pe-cerca-input pe-cerca-input--plantilla"
          />
        </div>
        <label className="pe-cerca-checkbox">
          <input
            type="checkbox"
            checked={cercaGlobal}
            onChange={(e) => setCercaGlobal(e.target.checked)}
          />
          Cercar a tot arreu
        </label>
        <label className="pe-cerca-checkbox">
          <input
            type="checkbox"
            checked={incloureSubcategories}
            onChange={(e) => setIncloureSubcategories(e.target.checked)}
          />
          Incloure subcategories
        </label>
      </div>

      {/* Resultats cerca categories */}
      {cercaCategoria.trim() && categoriesSearch.length > 0 && (
        <div className="pe-cerca-resultats">
          {categoriesSearch.map((c) => (
            <div
              key={c.id}
              className="pe-cerca-resultat-item"
              onClick={() => {
                onSelectCategoria({ id: parseInt(c.id), Nom: c.Nom, idPare: c.idPare });
                expandPathTo(c.id);
                setCercaCategoria("");
              }}
            >
              {getCategoryPath(c.id)}
            </div>
          ))}
        </div>
      )}

      {/* Info-line ruta actual */}
      {selectedCategoria && (
        <div className="pe-breadcrumb">
          📁 {getCategoryPath(selectedCategoria.id)}
        </div>
      )}

      {/* Contingut principal: arbre + llista */}
      <div className="pe-contingut">
        {/* Columna esquerra: arbre categories */}
        <div className="pe-categories-panel">
          <div className="pe-seccio-header">
            <span className="pe-seccio-titol">Categories</span>
            {!isSelectMode && (
              <>
                <button
                  className="Dashboard-btn-icona"
                  onClick={() => {
                    setNouCategoriaVisible(!nouCategoriaVisible);
                    setNouCategoriaNom("");
                  }}
                  title={selectedCategoria ? "Nova subcategoria" : "Nova categoria arrel"}
                >
                  +
                </button>
                <button
                  className="Dashboard-btn-icona"
                  onClick={() => {
                    if (selectedCategoria) {
                      setEditCategoriaId(parseInt(selectedCategoria.id));
                      setEditCategoriaNom(selectedCategoria.Nom);
                    }
                  }}
                  disabled={!selectedCategoria}
                  title="Renombrar"
                >
                  ✏️
                </button>
                <button
                  className="Dashboard-btn-icona"
                  onClick={handleEliminarCategoria}
                  disabled={!selectedCategoria}
                  title="Eliminar categoria"
                >
                  −
                </button>
              </>
            )}
          </div>

          {!isSelectMode && nouCategoriaVisible && (
            <div className="pe-inline-form">
              <input
                type="text"
                value={nouCategoriaNom}
                onChange={(e) => setNouCategoriaNom(e.target.value)}
                placeholder="Nom categoria..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCrearCategoria();
                  if (e.key === "Escape") setNouCategoriaVisible(false);
                }}
                autoFocus
                className="pe-inline-input"
              />
              <button className="Dashboard-btn-icona" onClick={handleCrearCategoria}>✓</button>
              <button className="Dashboard-btn-icona" onClick={() => setNouCategoriaVisible(false)}>✕</button>
            </div>
          )}

          {/* Categoria especial "Totes" per veure sense filtre */}
          <div
            className={`pe-node-totes ${!selectedCategoria ? "pe-node-totes--selected" : ""}`}
            onClick={() => onSelectCategoria(null)}
          >
            Totes les plantilles
          </div>

          {renderTree()}
        </div>

        {/* Columna dreta: llista plantilles */}
        <div className="pe-plantilles-panel">
          <div className="pe-seccio-header">
            <span className="pe-seccio-titol">
              Plantilles ({plantillesFiltrades.length})
            </span>
            {isSelectMode ? (
              <button
                className="Dashboard-btn Dashboard-btn-acceptar pe-btn-petit"
                onClick={() => {
                  if (selectedPlantilla && onSelectForVersion) {
                    onSelectForVersion(selectedPlantilla);
                  }
                }}
                disabled={!selectedPlantilla}
              >
                Seleccionar
              </button>
            ) : (
              !modeVinculacio && (
                <>
                  <button
                    className="Dashboard-btn-icona"
                    onClick={() => {
                      setNouPlantillaVisible(!nouPlantillaVisible);
                      setNouPlantillaNom("");
                      setNouPlantillaDesc("");
                    }}
                    title="Nova plantilla"
                  >
                    +
                  </button>
                  <button
                    className="Dashboard-btn-icona"
                    onClick={() => {
                      if (selectedPlantilla) {
                        setEditPlantillaId(parseInt(selectedPlantilla.id));
                        setEditPlantillaNom(selectedPlantilla.Nom);
                        setEditPlantillaDesc(selectedPlantilla.Descripcio || "");
                      }
                    }}
                    disabled={!selectedPlantilla}
                    title="Editar plantilla"
                  >
                    ✏️
                  </button>
                  <button
                    className="Dashboard-btn-icona"
                    onClick={handleEsborrarPlantilla}
                    disabled={!selectedPlantilla}
                    title="Esborrar plantilla"
                  >
                    −
                  </button>
                  <button
                    className="Dashboard-btn Dashboard-btn-acceptar pe-btn-petit"
                    onClick={handleIniciarVinculacio}
                    disabled={!selectedPlantilla}
                  >
                    Vincular
                  </button>
                  <button
                    className="Dashboard-btn Dashboard-btn-cancelar pe-btn-petit"
                    onClick={handleDesvincular}
                    disabled={!selectedPlantilla || !selectedCategoria}
                  >
                    Desvincular
                  </button>
                </>
              )
            )}
          </div>

          {/* Formulari nova plantilla */}
          {!isSelectMode && nouPlantillaVisible && (
            <div className="pe-form-plantilla pe-form-plantilla--nou">
              <input
                type="text"
                value={nouPlantillaNom}
                onChange={(e) => setNouPlantillaNom(e.target.value)}
                placeholder="Nom de la plantilla *"
                autoFocus
              />
              <input
                type="text"
                value={nouPlantillaDesc}
                onChange={(e) => setNouPlantillaDesc(e.target.value)}
                placeholder="Descripció (opcional)"
              />
              <div className="pe-form-btn-row">
                <button
                  className="Dashboard-btn Dashboard-btn-acceptar pe-form-btn-petit"
                  onClick={handleCrearPlantilla}
                >
                  Crear
                </button>
                <button
                  className="Dashboard-btn Dashboard-btn-cancelar pe-form-btn-petit"
                  onClick={() => setNouPlantillaVisible(false)}
                >
                  Cancel·lar
                </button>
              </div>
            </div>
          )}

          {/* Formulari editar plantilla */}
          {!isSelectMode && editPlantillaId && (
            <div className="pe-form-plantilla pe-form-plantilla--editar">
              <input
                type="text"
                value={editPlantillaNom}
                onChange={(e) => setEditPlantillaNom(e.target.value)}
                placeholder="Nom *"
                autoFocus
              />
              <input
                type="text"
                value={editPlantillaDesc}
                onChange={(e) => setEditPlantillaDesc(e.target.value)}
                placeholder="Descripció"
              />
              <div className="pe-form-btn-row">
                <button
                  className="Dashboard-btn Dashboard-btn-acceptar pe-form-btn-petit"
                  onClick={handleModificarPlantilla}
                >
                  Guardar
                </button>
                <button
                  className="Dashboard-btn Dashboard-btn-cancelar pe-form-btn-petit"
                  onClick={() => setEditPlantillaId(null)}
                >
                  Cancel·lar
                </button>
              </div>
            </div>
          )}

          {/* Taula de plantilles */}
          <div className="Dashboard-table-container">
            <table className="Dashboard-table">
              <thead>
                <tr>
                  {isSelectMode && <th style={{ width: 30 }}></th>}
                  <th style={{ width: 50 }}>ID</th>
                  <th>Nom</th>
                  <th>Descripció</th>
                  <th>Categories</th>
                  <th style={{ width: 70 }}>{isSelectMode ? "Sel." : "Partides"}</th>
                </tr>
              </thead>
              <tbody>
                {plantillesFiltrades.length === 0 ? (
                  <tr>
                    <td colSpan={isSelectMode ? 6 : 5} style={{ textAlign: "center", color: "#999" }}>
                      Cap plantilla trobada.
                    </td>
                  </tr>
                ) : (
                  plantillesFiltrades.map((p) => {
                    const isSelected =
                      selectedPlantilla &&
                      parseInt(selectedPlantilla.id) === parseInt(p.id);
                    const isPreviewOpen = isSelectMode && previewExpandedIds.includes(parseInt(p.id));
                    return (
                      <React.Fragment key={`${p.id}-${p.categoriaIds}`}>
                        <tr
                          className={isSelected ? "selected-row" : ""}
                          onClick={() => {
                            if (selectedPlantilla && parseInt(selectedPlantilla.id) === parseInt(p.id)) {
                              onSelectPlantilla(null);
                            } else {
                              onSelectPlantilla(p);
                            }
                          }}
                          onDoubleClick={() => {
                            onSelectPlantilla(p);
                            if (isSelectMode && onSelectForVersion) {
                              onSelectForVersion(p);
                            } else {
                              onGoToPartides();
                            }
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {isSelectMode && (
                            <td style={{ textAlign: "center", width: 30, padding: "2px" }}>
                              <button
                                className="Dashboard-btn-icona"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePreviewPartides(p.id);
                                }}
                                title={isPreviewOpen ? "Tancar partides" : "Veure partides"}
                                style={{ fontSize: "0.8em" }}
                              >
                                {isPreviewOpen ? "▼" : "▶"}
                              </button>
                            </td>
                          )}
                          <td>{p.id}</td>
                          <td>{p.Nom}</td>
                          <td>{p.Descripcio || ""}</td>
                          <td className="pe-td-categories">
                            {p.categoriaNames || "—"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {isSelectMode ? (
                              <button
                                className="Dashboard-btn-icona"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onSelectForVersion) onSelectForVersion(p);
                                }}
                                title="Seleccionar plantilla"
                              >
                                ✓
                              </button>
                            ) : (
                              <button
                                className="Dashboard-btn-icona"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectPlantilla(p);
                                  onGoToPartides();
                                }}
                                title="Editar partides"
                              >
                                📋
                              </button>
                            )}
                          </td>
                        </tr>
                        {isPreviewOpen && renderPreviewPartides(p.id)}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlantillesExplorador;
